import React, { useEffect, useState } from "react";
import { Pagination } from "antd";
import { useSelector } from "react-redux";

import InfoCard from "../../components/Common/InfoCard";
import TableHeader from "../../components/Common/TableHeader";
import LandLordTable from "../../components/Table/LandLordTable";
import { landlordPayouts } from "../../components/Data/landlordPayouts";
import Loader from "../../components/Common/Loader";
import NotFound from "../../components/NotFound";

import { getPaymentMonitoring } from "../../store/thunks/paymentThunks";
import { useThunk } from "../../hooks/useThunk";

// Helper functions
const formatPaymentDate = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
};

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "UGX 0";
    return `UGX ${amount.toLocaleString()}`;
};

const PayMonitoring = () => {

    const [search, setSearch] = useState("");
    const [selectedRange, setSelectedRange] = useState(""); // Used for dropdown-based date ranges
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10); // Default items per page


    const [doGetPaymentMonitoring, isGetting, paymentError] = useThunk(getPaymentMonitoring);

    const paymentData = useSelector((state) => state.payment.paymentMonitoring.data);
    const infoCard = useSelector((state) => state.payment.paymentMonitoring.infoCard);
    const pagination = useSelector((state) => state.payment.paymentMonitoring.pagination);

    const loadPaymentData = async (page = currentPage, pageSize = itemsPerPage) => {
        let startDate, endDate;
        if (selectedRange) {
            const now = new Date();
            switch (selectedRange) {
                case "Today":
                    startDate = new Date(now.setHours(0, 0, 0, 0));
                    endDate = new Date(now.setHours(23, 59, 59, 999));
                    break;
                case "This Week":
                    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
                    startDate = new Date(startOfWeek.setHours(0, 0, 0, 0));
                    endDate = new Date();
                    break;
                case "This Month":
                    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    endDate = new Date();
                    break;
                case "Last Month":
                    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                    startDate = lastMonth;
                    endDate = new Date(now.getFullYear(), now.getMonth(), 0);
                    break;
            }
        }
        const params = {
            searchTerm: search,
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null,
            offset: (page - 1) * pageSize, // Calculate offset
            limit: pageSize, // Items per page
        }
        const result = await doGetPaymentMonitoring(params);
    }

    useEffect(() => {
        setCurrentPage(1); // Reset to first page when searching
        loadPaymentData(1, itemsPerPage);
    }, [selectedRange]);

    const handlePageChange = (page, pageSize) => {
        setCurrentPage(page);
        if (pageSize !== itemsPerPage) {
            setItemsPerPage(pageSize);
        }
        loadPaymentData(page, pageSize);
    };

    const handleSearch = () => {
        setCurrentPage(1); // Reset to first page when searching
        loadPaymentData(1, itemsPerPage);
    };

    const transformedPayments = paymentData.map((item, index) => ({
        key: `payment-${index}`,
        id: formatPaymentDate(item.paymentDate), // "09/01 06:30" format
        paymentDate: item.paymentDate,
        tenantName: item.tenantName,
        property: item.unitProperty.replace('@', '/'), // Replacing "@" with "/"
        landlordName: item.landlordName,
        paid: formatCurrency(item.amount), // "UGX 950,000" format
        transactionStatus: item.transactionStatus === "SUCCESS" ? "Paid" : item.transactionStatus, 
        payments: [{
            amountLandlord: item.amountLandlord ? formatCurrency(item.amountLandlord) : "N/A",
            platformCommission: item.platformCommission ? formatCurrency(item.platformCommission) : "N/A",
            paymentMethod: item.paymentMethod,
            transactionId: item.transactionId,
        }],
    }));

    const columns = [
        { title: "Date / Time", dataIndex: "id", key: "id" },
        { title: "Tenant Name", dataIndex: "tenantName", key: "tenantName" },
        { title: "Unit / Property", dataIndex: "property", key: "property" },
        { title: "Transaction Status", dataIndex: "transactionStatus", key: "transactionStatus",
            render: (status) => (
              <span
                className={`text-[8px] sm:text-[10px] md:text-[11px] lg:text-[12px] px-2 py-1 rounded font-medium ${
                  status === "Paid"
                    ? "bg-[#DCFCE7] text-[#3fab67]"
                    : status === "PROCESSING"
                        ? "bg-[#E0F4FF] text-[#0078D4]"
                        : status === "FAILED"
                        ? "bg-[#FEE2E2] text-[#EF4444]"
                        : "bg-[#F1F5F9] text-[#9E9E9E]"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
              </span>
            ),
          },
        { title: "Landlord Name", dataIndex: "landlordName", key: "landlordName" },
        { title: "Amount Paid", dataIndex: "paid", key: "paid" },
    ];

    const subTableConfig = {
        dataField: "payments",
        columns: [
            {
                title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Amount to Landlord</span>,
                dataIndex: "amountLandlord",
                key: "amountLandlord",
            },
            {
                title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Platform Commission</span>,
                dataIndex: "platformCommission",
                key: "platformCommission",
            },
            {
                title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Payment Method</span>,
                dataIndex: "paymentMethod",
                key: "paymentMethod",
            },
            {
                title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Reference / Chanel</span>,
                dataIndex: "transactionId",
                key: "transactionId",
            },
        ],
    };

    return (
        <div className="min-h-screen p-4 sm:p-6 md:p-[30px]">
            <TableHeader
                searchValue={search}
                onSearchChange={setSearch}
                onSearchSubmit={handleSearch}
                searchPlaceholder="Search by landlord"
                dateRangeFilter={{
                    value: selectedRange,
                    onChange: setSelectedRange,
                    options: [
                        { label: "Today", value: "Today" },
                        { label: "This Week", value: "This Week" },
                        { label: "This Month", value: "This Month" },
                        { label: "Last Month", value: "Last Month" },
                    ],
                }}
                buttons={[
                    {
                        label: "Search",
                        onClick: handleSearch,
                        loading:isGetting,
                        disabled: isGetting,
                        className:
                            "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] uppercase w-[100px] ",
                    },
                ]}
                containerClass="bg-white p-3 sm:p-4 md:p-6 lg:p-[30px] gap-3 md:gap-3 lg:gap-[17px] grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 [@media(min-width:1050px)]:grid-cols-2 [@media(min-width:1250px)]:flex [@media(min-width:1460px)]:flex "
                searchInputClass="text-gray-600 md:w-full lg:w-full [@media(min-width:1250px)]:w-[400px] [@media(min-width:1460px)]:w-[636px]"
                datePickerClass="gap-2"
                filterDropdownClass="md:w-full lg:w-full [@media(min-width:1250px)]:w-[270px]"
                buttonWrapperClass="justify-start flex [@media(min-width:1250px)]:justify-end "
            />

            <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
                {/* Stat Cards */}
                <div className="grid gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[52px] gap-y-[30px] $ grid-cols-2 xl:grid-cols-4 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                        <InfoCard
                            label="Total Landlords Listed"
                            value={infoCard.totalUniqueLandlords}
                            className=" max-h-[100px] mx-auto"
                            labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            valueStyle="font-inter font-semibold text-[25px] lg:text-[40px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                        />
                    </div>
                    <div>
                        <InfoCard
                            label="Total Properties Listed"
                            value={infoCard.totalUniqueProperties}
                            className=" max-h-[100px] mx-auto"
                            labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            valueStyle="font-inter font-semibold text-[25px] lg:text-[40px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                        />
                    </div>
                    <div>
                        <InfoCard
                            label="Units Occupied"
                            value={infoCard.occupiedUnits}
                            className=" max-h-[100px] mx-auto"
                            labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            valueStyle="font-inter font-semibold text-[25px] lg:text-[40px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                        />
                    </div>
                    <div>
                        <InfoCard
                            label="Units Vacant"
                            value={infoCard.vacantUnits}
                            className=" max-h-[100px] mx-auto"
                            labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                            valueStyle="font-inter font-semibold text-[25px] lg:text-[40px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[8px] border border-[#E5E5E5]">
                {isGetting ? (
                    <Loader color="#4772F1" />
                ) : paymentData.length < 1 ? (
                    <NotFound
                        text1="No payments found"
                        text2="No payment data matches your current filter criteria."
                        isButton={false}
                    />
                ) : paymentError ? (
                    <NotFound
                        text1="Error fetching payments"
                        text2={paymentError?.message || "An error occurred while fetching payment data."}
                        isButton={false}
                    />
                ) : (
                    <LandLordTable
                        dataSource={transformedPayments}
                        columns={columns}
                        hasSubTable={true}
                        subTableConfig={subTableConfig}
                        rowKey="key"
                    />
                )}
            </div>

            {!isGetting && !(paymentData.length < 1) && !paymentError && (
                <div className="flex justify-center mt-6">
                    <Pagination
                        current={currentPage}
                        total={pagination?.totalDocuments || 0}
                        pageSize={itemsPerPage}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        hideOnSinglePage={false}
                        showLessItems
                        className="custom-pagination"
                    />
                </div>
            )}
        </div>
    );
};

export default PayMonitoring;
